import { ReactNode, useState, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { FormFieldBase, type RenderInputProps } from "@core/features/form";
import { useFormContext } from "react-hook-form";
import { Label } from "@themes/pollinator/ui/Label";
import { FieldError } from "@themes/pollinator/ui/FieldError";
import { Type, TypeVariant } from "@themes/pollinator/ui/Type";
import { ThemeType } from "@core/types/style.types";

// Import pure UI components
import { Input, type SizeType } from "@themes/pollinator/ui/Input";
import { Textarea } from "@themes/pollinator/ui/Textarea";
import { Checkbox } from "@themes/pollinator/ui/Checkbox";
import { Select, OptionType } from "@themes/pollinator/ui/Select";
import { RadioGroup } from "@themes/pollinator/ui/RadioGroup";
import { YesNoButtons } from "@themes/pollinator/ui/YesNoButtons";
import { SelectChips } from "@themes/pollinator/ui/SelectChips";
import { TimezoneSelect } from "@themes/pollinator/ui/TimezoneSelect";
import { PhotoUploadInput } from "@themes/pollinator/ui/PhotoUploadInput";
import { ResumeUploadInput } from "@themes/pollinator/ui/ResumeUploadInput";
import { PhoneInput } from "@themes/pollinator/ui/PhoneInput";
import { AffiliationList } from "@platform/features/affiliations";

// Component type mapping
const componentRegistry = {
  Input,
  Textarea,
  Checkbox,
  Select,
  RadioGroup,
  YesNoButtons,
  SelectChips,
  TimezoneSelect,
  PhotoUploadInput,
  ResumeUploadInput,
  PhoneInput,
  AffiliationList,
} as const;

export type FormComponentType = keyof typeof componentRegistry;

export type Props = {
  /** Field name - must match a key in the form schema */
  name: string;
  /** Component type to render */
  component: FormComponentType;
  /** Field label */
  label?: string;
  /** Help text shown below label */
  helpText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Icon to show inside the input */
  icon?: ReactNode;
  /** Prefix text shown before input */
  prefix?: string;
  /** Image to show above the field */
  image?: string;
  /** Layout direction */
  layout?: "vertical" | "horizontal";
  /** Label text variant */
  labelVariant?: TypeVariant;
  /** Additional CSS classes for wrapper */
  className?: string;
  /** Additional CSS classes for inner container */
  innerClassName?: string;
  /** Hide error message (still shows error icon) */
  isHideErrorMessage?: boolean;
  /** Hide error icon (for custom components) */
  isCustomComponent?: boolean;
  /** Transform value on change (for Input) */
  transformOnChange?: (val: string) => string;
  /** Autocomplete attribute (for Input) */
  autoComplete?: React.HTMLInputAutoCompleteAttribute;
  /** Options for Select, RadioGroup, YesNoButtons, SelectChips */
  options?: any[];
  /** Max items for RadioGroup, SelectChips */
  maxItems?: number;
  /** Whether Select allows multiple selections */
  isMulti?: boolean;
  /** Whether Select allows creating new options */
  isCreatable?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Theme variant for light/dark backgrounds (passed to Input) */
  theme?: ThemeType;
  /** Size variant (passed to Input) */
  size?: SizeType;
  /** Success state (passed to Input) */
  success?: boolean;
  /** Any additional component-specific props */
  [key: string]: any;
};

/**
 * Styled form field component that combines RHF binding with label and error display.
 * Wraps FormFieldBase with styled components from shared/ui.
 *
 * @example
 * <Form form={form}>
 *   <FormField name="email" label="Email" component="Input" required />
 *   <FormField name="message" label="Message" component="Textarea" />
 *   <FormField
 *     name="country"
 *     label="Country"
 *     component="Select"
 *     options={countries}
 *   />
 * </Form>
 */
export function FormField({
  name,
  component,
  label,
  helpText,
  required = false,
  disabled = false,
  icon,
  prefix,
  image,
  layout = "vertical",
  labelVariant,
  className,
  innerClassName,
  isHideErrorMessage = false,
  isCustomComponent = false,
  transformOnChange,
  autoComplete,
  options,
  maxItems,
  isMulti,
  isCreatable,
  placeholder,
  theme,
  size,
  success,
  ...componentProps
}: Props) {
  const Component = componentRegistry[component];

  if (!Component) {
    throw new Error(`Invalid component type: ${component}`);
  }

  // Build component-specific props based on component type
  const buildFieldProps = (baseProps: RenderInputProps) => {
    // Note: We explicitly exclude 'required' from being passed to the native input
    // because we use Zod for validation, not HTML5 validation.
    const { required: _required, ...restComponentProps } =
      componentProps as Record<string, any>;

    const fieldProps: Record<string, any> = {
      name: baseProps.name,
      id: baseProps.id,
      disabled: baseProps.disabled,
      error: baseProps.error,
      "aria-describedby": baseProps["aria-describedby"],
      "aria-labelledby": baseProps["aria-labelledby"],
      "aria-required": baseProps["aria-required"],
      ...restComponentProps,
    };

    // Handle value/onChange based on component type
    switch (component) {
      case "Checkbox":
        fieldProps.checked = !!baseProps.value;
        fieldProps.onChange = baseProps.onChange;
        fieldProps.onBlur = baseProps.onBlur;
        break;

      case "Select":
        // Select uses OptionType objects, but RHF stores arrays of values
        fieldProps.options = options;
        fieldProps.isMulti = isMulti;
        fieldProps.isCreatable = isCreatable;
        fieldProps.placeholder = placeholder;
        fieldProps.value = isMulti
          ? options?.filter((o: OptionType) =>
              baseProps.value?.includes(o.value)
            )
          : options?.find(
              (o: OptionType) => o.value === baseProps.value?.[0]
            ) || null;
        fieldProps.onChange = (selected: OptionType | OptionType[] | null) => {
          if (Array.isArray(selected)) {
            baseProps.onChange(selected.map((o) => o.value));
          } else if (selected) {
            baseProps.onChange([selected.value]);
          } else {
            baseProps.onChange([]);
          }
        };
        fieldProps.onBlur = baseProps.onBlur;
        break;

      case "RadioGroup":
      case "YesNoButtons":
      case "SelectChips":
        fieldProps.options = options;
        fieldProps.value = baseProps.value ?? [];
        fieldProps.onChange = baseProps.onChange;
        fieldProps.onBlur = baseProps.onBlur;
        if (maxItems !== undefined) {
          fieldProps.maxItems = maxItems;
        }
        break;

      case "Input":
        fieldProps.value = baseProps.value ?? "";
        fieldProps.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let newValue = e.target.value;
          if (transformOnChange) {
            newValue = transformOnChange(newValue);
          }
          baseProps.onChange(newValue);
        };
        fieldProps.onBlur = baseProps.onBlur;
        fieldProps.isWithIcon = !!icon;
        fieldProps.prefix = !!prefix;
        fieldProps.autoComplete = autoComplete;
        fieldProps.placeholder = placeholder;
        if (theme) fieldProps.theme = theme;
        if (size) fieldProps.size = size;
        if (success) fieldProps.success = success;
        break;

      case "Textarea":
        fieldProps.value = baseProps.value ?? "";
        fieldProps.onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          baseProps.onChange(e.target.value);
        };
        fieldProps.onBlur = baseProps.onBlur;
        fieldProps.placeholder = placeholder;
        break;

      case "TimezoneSelect":
        // TimezoneSelect uses OptionType objects, but RHF stores arrays of values
        fieldProps.options = options;
        fieldProps.placeholder = placeholder;
        if (size) fieldProps.size = size;
        fieldProps.value =
          options?.find((o: OptionType) => o.value === baseProps.value?.[0]) ||
          null;
        fieldProps.onChange = (selected: OptionType | null) => {
          if (selected) {
            baseProps.onChange([selected.value]);
          } else {
            baseProps.onChange([]);
          }
        };
        fieldProps.onBlur = baseProps.onBlur;
        break;

      case "AffiliationList":
        fieldProps.value = baseProps.value ?? [];
        fieldProps.onChange = baseProps.onChange;
        fieldProps.onBlur = baseProps.onBlur;
        fieldProps.maxItems = maxItems;
        fieldProps.organizationOptions = restComponentProps.organizationOptions;
        fieldProps.roleOptions = restComponentProps.roleOptions;
        fieldProps.isLoading = restComponentProps.isLoading;
        fieldProps.onCreateOrganization = restComponentProps.onCreateOrganization;
        break;

      case "PhotoUploadInput":
        fieldProps.value = baseProps.value;
        fieldProps.onChange = baseProps.onChange;
        fieldProps.onBlur = baseProps.onBlur;
        // Pass altText to the component for the img alt attribute
        fieldProps.altText = baseProps.value?.altText ?? "";
        break;

      case "PhoneInput":
        // PhoneInput stores E.164 format strings directly
        fieldProps.value = baseProps.value ?? "";
        fieldProps.onChange = baseProps.onChange;
        fieldProps.onBlur = baseProps.onBlur;
        fieldProps.placeholder = placeholder;
        break;

      default:
        // Default handling for other components
        fieldProps.value = baseProps.value;
        fieldProps.onChange = baseProps.onChange;
        fieldProps.onBlur = baseProps.onBlur;
        if (options) fieldProps.options = options;
        if (placeholder) fieldProps.placeholder = placeholder;
        break;
    }

    return fieldProps;
  };

  // Check if this is a PhotoUploadInput with requireAltText
  const isPhotoWithAltText =
    component === "PhotoUploadInput" && componentProps.requireAltText;
  const altTextLabel = `${label || "Photo"} Alt Text`;

  return (
    <>
      <FormFieldBase
        name={name}
        required={required}
        disabled={disabled}
        renderWrapper={({ children }) => (
          <div
            className={twMerge(
              "relative flex flex-col gap-1 transition-opacity duration-100 p-1",
              layout === "horizontal" && "md:flex-row md:items-start",
              className,
              disabled && "opacity-50"
            )}
          >
            {image && (
              <img
                src={image}
                alt={label}
                className="w-32 object-contain mx-auto mb-8"
              />
            )}
            {children}
          </div>
        )}
        renderLabel={({ error, required: isRequired }) =>
          label ? (
            <div
              className={twMerge(
                "flex flex-col gap-1 md:gap-2",
                layout === "horizontal" &&
                  "mb-0.5 w-full md:mb-0 md:mr-8 md:w-1/3"
              )}
            >
              <Label
                htmlFor={name}
                id={`${name}-label`}
                error={error}
                required={isRequired}
                typeVariant={labelVariant}
              >
                {label}
              </Label>
              {helpText && (
                <Type variant="caption" className="text-brand-700">
                  {helpText}
                </Type>
              )}
            </div>
          ) : null
        }
        renderInput={(baseProps) => {
          const fieldProps = buildFieldProps(baseProps);

          return (
            <div
              className={twMerge(
                "relative flex w-full items-center",
                layout === "horizontal" &&
                  "flex w-full flex-col gap-4 md:w-2/3 md:flex-row"
              )}
            >
              {icon && (
                <span className="absolute left-3 top-3 flex items-center w-5 h-5 text-brand-700 z-10">
                  {icon}
                </span>
              )}

              <div
                className={twMerge(
                  "flex flex-col relative w-full gap-1.5",
                  prefix && "flex-row gap-0",
                  innerClassName
                )}
              >
                {prefix && (
                  <span
                    className={twMerge(
                      "bg-brand-50 border-brand-800 text-brand-700 pointer-events-none flex items-center rounded-bl-sm rounded-tl-sm border border-r-0",
                      size === "hero" ? "px-4 py-3 text-lg" : "px-3 py-2 text-sm"
                    )}
                  >
                    {prefix}
                  </span>
                )}

                {/* @ts-expect-error - Dynamic component props are validated at runtime via buildFieldProps */}
                <Component {...fieldProps} />
              </div>
            </div>
          );
        }}
        renderError={({ id, message }) =>
          !isHideErrorMessage && message ? (
            <FieldError id={id} message={message} />
          ) : null
        }
      />
      {isPhotoWithAltText && (
        <PhotoAltTextField
          name={name}
          label={altTextLabel}
          layout={layout}
          labelVariant={labelVariant}
          disabled={disabled}
          className={className}
        />
      )}
    </>
  );
}

/**
 * Internal component for rendering alt text field for PhotoUploadInput.
 * Uses the form context to read/write the altText property of the photo value.
 */
function PhotoAltTextField({
  name,
  label,
  layout,
  labelVariant,
  disabled,
  className,
}: {
  name: string;
  label: string;
  layout: "vertical" | "horizontal";
  labelVariant?: TypeVariant;
  disabled: boolean;
  className?: string;
}) {
  const { watch, setValue, trigger } = useFormContext();
  const photoValue = watch(name);
  const [touched, setTouched] = useState(false);

  const altText = photoValue?.altText ?? "";
  const hasImage = !!photoValue?.file;
  const showError = touched && hasImage && (!altText || altText.trim() === "");

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (photoValue) {
        setValue(name, { ...photoValue, altText: e.target.value }, { shouldDirty: true });
      }
    },
    [photoValue, setValue, name]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    trigger(name);
  }, [trigger, name]);

  // Don't render if no image
  if (!hasImage) {
    return null;
  }

  const altTextId = `${name}-altText`;

  // Match FormFieldBase structure exactly:
  // - Outer wrapper with flex-row in horizontal layout
  // - Label with md:w-1/3
  // - Content wrapper with w-full containing input and error
  return (
    <div
      className={twMerge(
        "relative flex flex-col gap-1 transition-opacity duration-100 p-1",
        layout === "horizontal" && "md:flex-row md:items-start",
        className,
        disabled && "opacity-50"
      )}
    >
      {/* Label section - matches FormField's renderLabel structure */}
      <div
        className={twMerge(
          "flex flex-col gap-2",
          layout === "horizontal" && "mb-0.5 w-full md:mb-0 md:mr-8 md:w-1/3"
        )}
      >
        <Label
          htmlFor={altTextId}
          id={`${altTextId}-label`}
          error={showError}
          required
          typeVariant={labelVariant}
        >
          {label}
        </Label>
      </div>

      {/* Content wrapper - matches FormFieldBase's structure */}
      <div className="flex flex-col gap-2 w-full">
        {/* Input wrapper - matches FormField's renderInput structure */}
        <div
          className={twMerge(
            "relative flex w-full items-center",
            layout === "horizontal" && "flex w-full flex-col gap-4 md:w-2/3 md:flex-row"
          )}
        >
          <div className="flex flex-col relative w-full gap-1.5">
            <Input
              id={altTextId}
              name={altTextId}
              value={altText}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Describe this image for accessibility"
              error={showError}
              disabled={disabled}
              aria-labelledby={`${altTextId}-label`}
              aria-describedby={showError ? `${altTextId}-error` : undefined}
              aria-invalid={showError}
              aria-required
            />
          </div>
        </div>
        {/* Error inside content wrapper */}
        {showError && (
          <FieldError id={`${altTextId}-error`} message="Alt text is required" />
        )}
      </div>
    </div>
  );
}

FormField.displayName = "FormField";

export default FormField;
